import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import {
  parseDeepLinkPayload,
  parseWorkspaceUrl,
  serializeWorkspaceUrl,
  DEEP_LINK_ROUTES,
  URL_STATE_REPLACE_DEBOUNCE_MS,
} from '../deepLinks';
import { EVENT_TYPES, detectActiveNavigationPreset } from '../features/registry';

/**
 * Atlas workspace view state: view tab, story panel, selection, pulse rail, URL sync.
 * URL state completo (3.7): período, types, lens, isolatedLayer + debounce replaceState.
 */
export function useAtlasWorkspace({
  atlasVivoActive,
  currentFilters,
  setCurrentFilters,
  calendarDate,
  selectCalendarDayOnly,
  selectCalendarDate,
  onQuickAdd,
}) {
  const initialWorkspace = parseWorkspaceUrl(
    typeof window !== 'undefined' ? window.location.search : '',
  );

  const [workspaceView, setWorkspaceView] = useState(() => initialWorkspace.view);
  const [activeStoryKey, setActiveStoryKey] = useState(() => initialWorkspace.story);
  const [atlasSelection, setAtlasSelection] = useState(null);
  const [railHoverPosition, setRailHoverPosition] = useState(null);
  const [railFocusPosition, setRailFocusPosition] = useState(null);
  const [railWorkspaceRequest, setRailWorkspaceRequest] = useState(0);
  const workspaceHistorySyncRef = useRef(false);
  const replaceStateTimerRef = useRef(null);

  const applyWorkspaceFromUrl = useCallback((workspace, options = {}) => {
    if (!workspace) return;
    const { resetFilters = false } = options;
    if (workspace.view) setWorkspaceView(workspace.view);

    setCurrentFilters((prev) => {
      const next = { ...prev };
      if (workspace.start) next.startDate = workspace.start;
      if (workspace.end) next.endDate = workspace.end;
      if (workspace.types && workspace.types.length > 0) {
        next.selectedTypes = workspace.types;
      } else if (resetFilters) {
        next.selectedTypes = EVENT_TYPES.map((et) => et.key);
      }
      if (workspace.isolatedLayer) {
        next.focusedKey = workspace.isolatedLayer;
      } else if (resetFilters) {
        next.focusedKey = null;
      }
      return next;
    });

    if (workspace.date) {
      const parsed = moment(workspace.date, 'YYYY-MM-DD', true);
      if (parsed.isValid()) selectCalendarDayOnly(parsed.toDate());
    }
    const nextView = workspace.view;
    if (nextView === 'historias' || nextView === 'dados') {
      setActiveStoryKey(workspace.story || null);
    } else if (nextView) {
      setActiveStoryKey(null);
    } else if (Object.prototype.hasOwnProperty.call(workspace, 'story')) {
      setActiveStoryKey(workspace.story || null);
    }
  }, [selectCalendarDayOnly, setCurrentFilters]);

  const handleGoToImport = useCallback(() => {
    setWorkspaceView('dados');
    setActiveStoryKey('import-health');
  }, []);

  useEffect(() => {
    if (workspaceView !== 'historias' && workspaceView !== 'dados') {
      setActiveStoryKey(null);
    }
  }, [workspaceView]);

  useEffect(() => {
    if (!atlasVivoActive) return undefined;

    const handlePopState = () => {
      workspaceHistorySyncRef.current = true;
      applyWorkspaceFromUrl(parseWorkspaceUrl(window.location.search), { resetFilters: true });
      workspaceHistorySyncRef.current = false;
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [atlasVivoActive, applyWorkspaceFromUrl]);

  useEffect(() => {
    if (!atlasVivoActive || workspaceHistorySyncRef.current) return undefined;

    const lens = detectActiveNavigationPreset(currentFilters.selectedTypes);

    const nextSearch = serializeWorkspaceUrl(
      {
        view: workspaceView,
        start: currentFilters.startDate,
        end: currentFilters.endDate,
        date: moment(calendarDate).format('YYYY-MM-DD'),
        story: (workspaceView === 'historias' || workspaceView === 'dados') ? activeStoryKey : null,
        types: currentFilters.selectedTypes,
        lens,
        isolatedLayer: currentFilters.focusedKey || null,
      },
      window.location.search,
    );
    const nextUrl = `${window.location.pathname}${nextSearch}${window.location.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (nextUrl === currentUrl) return undefined;

    if (replaceStateTimerRef.current) {
      clearTimeout(replaceStateTimerRef.current);
    }
    replaceStateTimerRef.current = setTimeout(() => {
      replaceStateTimerRef.current = null;
      if (workspaceHistorySyncRef.current) return;
      const latest = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (nextUrl !== latest) {
        window.history.replaceState(null, '', nextUrl);
      }
    }, URL_STATE_REPLACE_DEBOUNCE_MS);

    return () => {
      if (replaceStateTimerRef.current) {
        clearTimeout(replaceStateTimerRef.current);
        replaceStateTimerRef.current = null;
      }
    };
  }, [
    atlasVivoActive,
    workspaceView,
    currentFilters.startDate,
    currentFilters.endDate,
    currentFilters.selectedTypes,
    currentFilters.focusedKey,
    calendarDate,
    activeStoryKey,
  ]);

  useEffect(() => {
    let removeListener;
    let cancelled = false;

    import('@capacitor/app').then(({ App: CapacitorApp }) => {
      if (cancelled) return;
      CapacitorApp.addListener('appUrlOpen', ({ url }) => {
        const { route, workspace } = parseDeepLinkPayload(url);
        if (route === DEEP_LINK_ROUTES.TODAY) {
          selectCalendarDate(new Date());
          if (atlasVivoActive) {
            applyWorkspaceFromUrl({
              ...workspace,
              view: 'dia',
              date: moment().format('YYYY-MM-DD'),
            });
          }
          return;
        }
        if (route === DEEP_LINK_ROUTES.QUICK_ADD) {
          if (onQuickAdd) onQuickAdd(workspace);
          if (atlasVivoActive) {
            applyWorkspaceFromUrl(workspace);
          }
          return;
        }
        if (route && atlasVivoActive) {
          applyWorkspaceFromUrl(workspace);
        }
      }).then((handle) => { removeListener = handle; });
    }).catch(() => {});

    return () => {
      cancelled = true;
      if (removeListener) removeListener.remove();
    };
  }, [selectCalendarDate, atlasVivoActive, applyWorkspaceFromUrl, onQuickAdd]);

  return {
    workspaceView,
    setWorkspaceView,
    activeStoryKey,
    setActiveStoryKey,
    atlasSelection,
    setAtlasSelection,
    railHoverPosition,
    setRailHoverPosition,
    railFocusPosition,
    setRailFocusPosition,
    railWorkspaceRequest,
    setRailWorkspaceRequest,
    applyWorkspaceFromUrl,
    handleGoToImport,
  };
}
